import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  ArrowLeft, Plus, Trash2, Edit2, Save, X, Brain,
  ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminQuizzes = () => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedQuiz, setExpandedQuiz] = useState(null);
  const [filter, setFilter] = useState({ age_group: '', subject: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newQuiz, setNewQuiz] = useState({
    age_group: 'elementary',
    subject: '',
    round_number: 1,
    questions: [{ question: '', options: ['', '', '', ''], correctAnswer: 0 }]
  });

  useEffect(() => {
    if (!localStorage.getItem('adminAuth')) {
      navigate('/admin');
      return;
    }
    fetchQuizzes();
  }, [navigate]);

  const fetchQuizzes = async () => {
    try {
      const response = await axios.get(`${API}/admin/quiz`);
      setQuizzes(response.data.quizzes);
    } catch (err) {
      console.error('Error fetching quizzes:', err);
    }
    setLoading(false);
  };

  const handleDelete = async (quizId) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) return;
    try {
      await axios.delete(`${API}/admin/quiz/${quizId}`);
      fetchQuizzes();
    } catch (err) {
      alert('Error deleting quiz');
    }
  };

  const handleAddQuiz = async () => {
    try {
      await axios.post(`${API}/admin/quiz`, newQuiz);
      setShowAddForm(false);
      setNewQuiz({
        age_group: 'elementary',
        subject: '',
        round_number: 1,
        questions: [{ question: '', options: ['', '', '', ''], correctAnswer: 0 }]
      });
      fetchQuizzes();
    } catch (err) {
      alert('Error creating quiz');
    }
  };

  const addQuestion = () => {
    setNewQuiz({
      ...newQuiz,
      questions: [...newQuiz.questions, { question: '', options: ['', '', '', ''], correctAnswer: 0 }]
    });
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...newQuiz.questions];
    if (field === 'option') {
      updated[index].options[value.index] = value.text;
    } else {
      updated[index][field] = value;
    }
    setNewQuiz({ ...newQuiz, questions: updated });
  };

  const filteredQuizzes = quizzes.filter(q => {
    if (filter.age_group && q.age_group !== filter.age_group) return false;
    if (filter.subject && !q.subject.includes(filter.subject)) return false;
    return true;
  });

  const groupedQuizzes = filteredQuizzes.reduce((acc, quiz) => {
    const key = `${quiz.age_group}-${quiz.subject}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(quiz);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-white hover:bg-white/20">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <Brain className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">Quiz Content</h1>
                <p className="text-purple-100">{quizzes.length} quiz rounds</p>
              </div>
            </div>
            <Button onClick={() => setShowAddForm(true)} className="bg-white text-purple-600 hover:bg-purple-50">
              <Plus className="w-4 h-4 mr-2" /> Add Quiz
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <select
            value={filter.age_group}
            onChange={(e) => setFilter({ ...filter, age_group: e.target.value })}
            className="px-4 py-2 rounded-lg border border-gray-300"
          >
            <option value="">All Age Groups</option>
            <option value="preschool">Preschool</option>
            <option value="elementary">Elementary</option>
            <option value="advanced">Advanced</option>
          </select>
          <Input
            placeholder="Search subject..."
            value={filter.subject}
            onChange={(e) => setFilter({ ...filter, subject: e.target.value })}
            className="max-w-xs"
          />
        </div>

        {/* Quiz List */}
        <div className="space-y-4">
          {Object.entries(groupedQuizzes).map(([key, rounds]) => (
            <Card key={key} className="p-4 bg-white shadow" style={{ borderRadius: '12px' }}>
              <div 
                className="flex justify-between items-center cursor-pointer"
                onClick={() => setExpandedQuiz(expandedQuiz === key ? null : key)}
              >
                <div>
                  <span className="px-2 py-1 rounded text-xs font-semibold bg-purple-100 text-purple-700 mr-2">
                    {rounds[0].age_group}
                  </span>
                  <span className="font-bold text-gray-800 capitalize">{rounds[0].subject}</span>
                  <span className="text-gray-500 ml-2">({rounds.length} rounds)</span>
                </div>
                {expandedQuiz === key ? <ChevronUp /> : <ChevronDown />}
              </div>
              
              {expandedQuiz === key && (
                <div className="mt-4 space-y-3">
                  {rounds.sort((a, b) => a.round_number - b.round_number).map((quiz) => (
                    <div key={quiz.quiz_id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Round {quiz.round_number}</span>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleDelete(quiz.quiz_id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{quiz.questions?.length || 0} questions</p>
                      <div className="mt-2 text-xs text-gray-500">
                        {quiz.questions?.slice(0, 2).map((q, i) => (
                          <div key={i}>• {q.question}</div>
                        ))}
                        {quiz.questions?.length > 2 && <div>...and {quiz.questions.length - 2} more</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>

        {/* Add Quiz Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 bg-white" style={{ borderRadius: '16px' }}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Add New Quiz</h2>
                <Button variant="ghost" onClick={() => setShowAddForm(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <select
                    value={newQuiz.age_group}
                    onChange={(e) => setNewQuiz({ ...newQuiz, age_group: e.target.value })}
                    className="px-3 py-2 rounded-lg border"
                  >
                    <option value="preschool">Preschool</option>
                    <option value="elementary">Elementary</option>
                    <option value="advanced">Advanced</option>
                  </select>
                  <Input
                    placeholder="Subject (e.g., math)"
                    value={newQuiz.subject}
                    onChange={(e) => setNewQuiz({ ...newQuiz, subject: e.target.value })}
                  />
                  <Input
                    type="number"
                    placeholder="Round #"
                    value={newQuiz.round_number}
                    onChange={(e) => setNewQuiz({ ...newQuiz, round_number: parseInt(e.target.value) })}
                  />
                </div>

                <div className="space-y-4">
                  {newQuiz.questions.map((q, qIndex) => (
                    <div key={qIndex} className="p-4 bg-gray-50 rounded-lg">
                      <Input
                        placeholder={`Question ${qIndex + 1}`}
                        value={q.question}
                        onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                        className="mb-3"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        {q.options.map((opt, oIndex) => (
                          <div key={oIndex} className="flex items-center gap-2">
                            <input
                              type="radio"
                              name={`correct-${qIndex}`}
                              checked={q.correctAnswer === oIndex}
                              onChange={() => updateQuestion(qIndex, 'correctAnswer', oIndex)}
                            />
                            <Input
                              placeholder={`Option ${oIndex + 1}`}
                              value={opt}
                              onChange={(e) => updateQuestion(qIndex, 'option', { index: oIndex, text: e.target.value })}
                              className="flex-1"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <Button onClick={addQuestion} variant="outline" className="w-full">
                  <Plus className="w-4 h-4 mr-2" /> Add Question
                </Button>

                <Button onClick={handleAddQuiz} className="w-full bg-purple-600 hover:bg-purple-700 text-white">
                  <Save className="w-4 h-4 mr-2" /> Save Quiz
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminQuizzes;
